import type { Action } from "@judo/actions";
import { useDispatchOptional, useElementDisabled, useElementVisibility, usePageContextOptional } from "@judo/core";
import { useConfirmation } from "@judo/feedback";
import { useModelLabel } from "@judo/i18n";
import type { Button as ButtonElement, PageDefinition } from "@judo/model-api";
import { getButtonTestId } from "@judo/test-ids";
import { Button } from "@mui/material";
import { getDestructiveConfirmationOptions, isDestructiveAction } from "../utils/destructive-action-utils";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import { IconRenderer } from "./IconRenderer";

export interface StandaloneButtonRendererProps {
	element: ButtonElement;
	/** Optional page override (uses context if not provided) */
	page?: PageDefinition;
	/** Optional dispatch override (uses context if not provided) */
	onDispatch?: (action: Action) => Promise<unknown>;
}

/**
 * Renderer for standalone Button elements (direct children of Flex containers).
 * These are typically relation navigation buttons like "View Matter Table".
 * The button's actionDefinition is matched against page.actions to find the Action to dispatch.
 */
export function StandaloneButtonRenderer({
	element,
	page: pageProp,
	onDispatch: onDispatchProp,
}: StandaloneButtonRendererProps) {
	// Check element visibility (hiddenBy dynamic data, static hidden, customization overrides)
	const hidden = useElementVisibility(element);
	// Check element disabled state (enabledBy dynamic data, static disabled, customization overrides)
	const disabled = useElementDisabled(element);

	// Use props if provided, otherwise fall back to context
	const pageContext = usePageContextOptional();
	const dispatchContext = useDispatchOptional();

	const page = pageProp ?? pageContext?.pageDefinition;
	const onDispatch = onDispatchProp ?? dispatchContext?.dispatch;
	const { confirm } = useConfirmation();

	const handleClick = async () => {
		// Find the corresponding Action from page.actions by matching actionDefinition
		const action = page ? findActionByDefinition(page, element.actionDefinition) : undefined;

		if (action && onDispatch) {
			const actionType = element.actionDefinition?.["@type"];
			if (isDestructiveAction(actionType)) {
				const confirmed = await confirm(getDestructiveConfirmationOptions(actionType!));
				if (!confirmed) return;
			}
			onDispatch(action as Action).catch((error) => {
				console.error("Button action failed:", error);
			});
		}
	};

	const testId = getButtonTestId(element);
	const { label } = useModelLabel(element);

	if (hidden) {
		return null;
	}

	// Map button style to MUI variant
	const getVariant = (): "contained" | "outlined" | "text" => {
		const style = element.buttonStyle;
		if (style === "outlined") return "outlined";
		if (style === "text") return "text";
		return "contained";
	};

	return (
		<Button
			variant={getVariant()}
			onClick={handleClick}
			disabled={disabled}
			data-testid={testId}
			startIcon={element.icon ? <IconRenderer icon={element.icon} /> : undefined}
		>
			{label}
		</Button>
	);
}
